import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { href: '#about', label: 'Approche' },
  { href: '#services', label: 'Services' },
  { href: '#casclients', label: 'Cas clients' },
  { href: '#pricing', label: 'Tarifs' },
  { href: '#avis', label: 'Avis' },
  { href: '#contact', label: 'Contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map(l => l.href.substring(1));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.2, rootMargin: '-80px 0px -40% 0px' }
    );
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    setActiveSection(href.substring(1));
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <div className="fixed top-0 left-0 w-full h-[2px] z-[60]" data-testid="scroll-progress">
        <div className="h-full transition-[width] duration-150 ease-out" style={{ width: `${scrollProgress}%`, background: 'linear-gradient(90deg, #1A6FFF, #00D9A8)' }} />
      </div>

      <motion.nav
        data-testid="main-navigation"
        className="fixed top-0 left-0 w-full z-50 transition-all duration-300"
        style={{ paddingTop: scrolled ? 8 : 16, paddingBottom: scrolled ? 8 : 16 }}
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-12">
          <div className={`flex items-center justify-between rounded-2xl px-5 py-2.5 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border border-black/[0.07] shadow-lg shadow-black/10' : ''}`}>
            <a href="#home" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center" data-testid="nav-logo">
              <img src="/logo-misora.png" alt="Misora" className="h-10 w-auto object-contain" />
            </a>

            <div className="hidden md:flex items-center gap-0.5">
              {NAV_LINKS.map(({ href, label }) => (
                <button key={href} onClick={() => scrollTo(href)} data-testid={`nav-link-${href.substring(1)}`}
                  aria-current={activeSection === href.substring(1) ? 'page' : undefined}
                  className={`relative px-3.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors duration-200 ${
                    activeSection === href.substring(1)
                      ? scrolled ? 'text-[#0B1829]' : 'text-white'
                      : scrolled ? 'text-[#0B1829]/50 hover:text-[#0B1829]' : 'text-white/45 hover:text-white/80'
                  }`}>
                  {activeSection === href.substring(1) && (
                    <motion.div
                      layoutId="activeNav"
                      className={`absolute inset-0 rounded-lg ${scrolled ? 'bg-[#0B1829]/[0.08]' : 'bg-white/[0.08]'}`}
                      transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
                    />
                  )}
                  <span className="relative z-10">{label}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button onClick={() => scrollTo('#contact')} data-testid="nav-cta-button"
                className="hidden md:block px-5 py-2 text-[13px] font-semibold text-white rounded-xl bg-gradient-to-r from-[#1A6FFF] to-[#00D9A8] hover:shadow-lg hover:shadow-blue-500/20 transition-all active:scale-95">
                Démarrer un projet
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-expanded={mobileOpen}
                aria-label="Menu mobile"
                data-testid="mobile-menu-toggle"
                className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-[#0B1829] hover:bg-[#0B1829]/10' : 'text-white hover:bg-white/10'}`}
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-20 z-40 mx-6" data-testid="mobile-menu">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 border border-black/[0.07] shadow-2xl">
              {NAV_LINKS.map(({ href, label }) => (
                <button key={href} onClick={() => scrollTo(href)}
                  className="block w-full text-left px-4 py-2.5 text-[#0B1829]/70 hover:text-[#0B1829] hover:bg-[#0B1829]/5 rounded-lg transition-all text-sm"
                  data-testid={`mobile-nav-${href.substring(1)}`}>{label}</button>
              ))}
              <button onClick={() => scrollTo('#contact')}
                className="mt-3 w-full px-5 py-3 text-sm font-semibold text-white rounded-xl bg-gradient-to-r from-[#1A6FFF] to-[#00D9A8] active:scale-95 transition-transform"
                data-testid="mobile-cta-button">Démarrer un projet</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
